import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import Index from "./pages/Index";

const Tours = lazy(() => import("./pages/Tours"));
const TourSlugPage = lazy(() => import("./pages/TourSlug"));
const ShareTourRedirect = lazy(() => import("./pages/ShareTourRedirect"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ProductPhotography = lazy(() => import("./pages/ProductPhotography"));
const TV = lazy(() => import("./pages/TV"));
const Agents = lazy(() => import("./pages/Agents"));
const Developers = lazy(() => import("./pages/Developers"));
const ProgressTracking = lazy(() => import("./pages/ProgressTracking"));
const ListProperty = lazy(() => import("./pages/ListProperty"));
const JoinAgents = lazy(() => import("./pages/JoinAgents"));
const Signup = lazy(() => import("./pages/Signup"));
const SignIn = lazy(() => import("./pages/SignIn"));
const OwnerPortal = lazy(() => import("./pages/OwnerPortal"));
const AgentDashboard = lazy(() => import("./pages/AgentDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboardExtended"));
const AdminDashboardLegacy = lazy(() => import("./pages/AdminDashboardLegacy"));
const SolicitorDueDiligence = lazy(() => import("./pages/SolicitorDueDiligence"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Start = lazy(() => import("./pages/Start"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <ErrorBoundary>
          <BrowserRouter>
            <div className="min-h-screen overflow-x-hidden">
              <ScrollToTop />
              <Suspense fallback={<PageLoader />}>
                <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/tours" element={<Tours />} />
              <Route path="/tour/:slug" element={<TourSlugPage />} />
              <Route path="/share/tour/:slug" element={<ShareTourRedirect />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/product-photography" element={<ProductPhotography />} />
              <Route path="/tv" element={<TV />} />
              <Route path="/agents" element={<Agents />} />
              <Route path="/developers" element={<Developers />} />
              <Route path="/progress-tracking" element={<ProgressTracking />} />
              <Route path="/list-property" element={<ListProperty />} />
              <Route path="/join-agents" element={<JoinAgents />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/owner-portal" element={<OwnerPortal />} />
              <Route path="/agent-dashboard" element={<AgentDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin-legacy" element={<AdminDashboardLegacy />} />
              <Route path="/solicitor" element={<SolicitorDueDiligence />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/start" element={<Start />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </div>
          </BrowserRouter>
        </ErrorBoundary>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
